import { Badge, type BadgeTone } from "../components/ui/Badge";
import { Card, CardBody } from "../components/ui/Card";
import { EmptyState } from "../components/ui/EmptyState";
import { ErrorState } from "../components/ui/ErrorState";
import { SourcesIcon } from "../components/ui/icons";
import { SkeletonScreen } from "../components/ui/Skeleton";
import { StatusDot } from "../components/ui/StatusDot";
import { monitoringFrequencyLabel, sourceHealthLabel, sourcesList } from "../copy/sq";
import { formatDate } from "../lib/date";
import { useSources } from "../hooks/useApi";
import type { SourceHealthStatus } from "../types/api";

const HEALTH_TONE: Record<SourceHealthStatus, BadgeTone> = {
  healthy: "good",
  degraded: "warn",
  unhealthy: "bad",
  unknown: "neutral",
};

export function SourcesScreen() {
  const { data, isLoading, isError, error, refetch } = useSources();

  if (isLoading) return <SkeletonScreen cards={5} />;

  if (isError) {
    return <ErrorState title={sourcesList.errorTitle} message={error instanceof Error ? error.message : undefined} onRetry={() => refetch()} />;
  }

  if (!data || data.items.length === 0) {
    return <EmptyState icon={<SourcesIcon width={32} height={32} />} title={sourcesList.emptyTitle} description={sourcesList.emptyDescription} />;
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {data.items.map((source) => (
        <Card key={source.id}>
          <CardBody>
            <div className="mb-2 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate font-serif text-sm font-semibold text-ink-950">{source.name}</p>
                {source.name_local ? <p className="truncate text-xs text-ink-400">{source.name_local}</p> : null}
              </div>
              <Badge tone={HEALTH_TONE[source.health]}>{sourceHealthLabel[source.health]}</Badge>
            </div>

            <div className="mb-3 flex flex-wrap gap-1.5">
              <Badge tone="neutral">{sourcesList.tier(source.tier)}</Badge>
              <Badge tone="neutral">{source.source_type}</Badge>
              {source.monitoring_frequency ? (
                <Badge tone="neutral">{monitoringFrequencyLabel[source.monitoring_frequency] ?? source.monitoring_frequency}</Badge>
              ) : null}
            </div>

            <div className="space-y-1 text-xs text-ink-500">
              <StatusDot
                tone={source.last_successful_check_at ? "good" : "neutral"}
                label={
                  source.last_successful_check_at
                    ? sourcesList.lastSuccessfulCheck(formatDate(source.last_successful_check_at))
                    : sourcesList.neverChecked
                }
              />
              {source.consecutive_failures > 0 ? (
                <p className="text-ink-500">{sourcesList.consecutiveFailures(source.consecutive_failures)}</p>
              ) : null}
            </div>

            {source.latest_failure_reason ? (
              <p className="mt-2.5 rounded-md bg-signal-bad-bg/60 px-2.5 py-2 text-xs leading-relaxed text-signal-bad">{source.latest_failure_reason}</p>
            ) : null}
          </CardBody>
        </Card>
      ))}
    </div>
  );
}
